// 测试码验证服务

import { testCodeStorage, globalSettings } from './persistentStorage';

export interface TestCode {
  code: string;
  isUsed: boolean;
  usedAt?: number;
  expiresAt?: number;
  assessmentType?: 'emotional-memory-pattern' | 'big-five-personality'; // 新增：测评类型，默认为情绪记忆测评
}

// 临时的测试码数据 - 后续会替换为真实API
const TEMP_VALID_CODES: TestCode[] = [
  { code: 'TEST01', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'TEST02', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'DEMO01', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'DEMO02', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'SAMPLE', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'TRIAL1', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'TRIAL2', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'BETA01', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'BETA02', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  { code: 'ALPHA1', isUsed: false, assessmentType: 'emotional-memory-pattern' },
  // 大五人格测评测试码
  { code: 'BIG501', isUsed: false, assessmentType: 'big-five-personality' },
  { code: 'BIG502', isUsed: false, assessmentType: 'big-five-personality' },
  { code: 'PERSON1', isUsed: false, assessmentType: 'big-five-personality' },
  { code: 'PERSON2', isUsed: false, assessmentType: 'big-five-personality' },
  { code: 'TRAIT01', isUsed: false, assessmentType: 'big-five-personality' },
];

// 从本地存储获取测试码状态
const getStoredCodes = (): TestCode[] => {
  try {
    const stored = localStorage.getItem('testCodes');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('读取测试码数据失败:', error);
  }
  return TEMP_VALID_CODES;
};

// 保存测试码状态到本地存储
const saveStoredCodes = (codes: TestCode[]) => {
  try {
    localStorage.setItem('testCodes', JSON.stringify(codes));
  } catch (error) {
    console.error('保存测试码数据失败:', error);
  }
};

// 获取管理员生成的测试码
const getAdminCodes = async (): Promise<TestCode[]> => {
  try {
    const adminCodes = await testCodeStorage.getTestCodes();
    return adminCodes.map((adminCode: any) => ({
      code: adminCode.code,
      isUsed: adminCode.status === 'used' || adminCode.isUsed,
      usedAt: adminCode.usedAt ? new Date(adminCode.usedAt).getTime() : undefined,
      assessmentType: adminCode.assessmentType || 'emotional-memory-pattern' // 向后兼容：默认为情绪记忆测评
    }));
  } catch (error) {
    console.error('读取管理员测试码失败:', error);
    return [];
  }
};

// 验证测试码
export const validateTestCode = async (inputCode: string, assessmentType: 'emotional-memory-pattern' | 'big-five-personality' = 'emotional-memory-pattern'): Promise<{
  isValid: boolean;
  message: string;
  code?: TestCode;
}> => {
  // 模拟API调用延迟
  await new Promise(resolve => setTimeout(resolve, 800));
  
  const inputCodeUpper = inputCode.toUpperCase();
  
  // 检查内置测试码
  const builtInCodes = getStoredCodes();
  let code = builtInCodes.find(c => c.code === inputCodeUpper);
  let isAdminCode = false;
  
  // 如果内置测试码中没找到，检查管理员生成的测试码
  if (!code) {
    const adminCodes = await getAdminCodes();
    code = adminCodes.find(c => c.code === inputCodeUpper);
    isAdminCode = true;
  }
  
  if (!code) {
    return {
      isValid: false,
      message: '测试码不存在，请检查后重新输入'
    };
  }
  
  if (code.isUsed) {
    return {
      isValid: false,
      message: '此测试码已被使用，请联系管理员获取新的测试码'
    };
  }
  
  // 检查是否过期
  if (code.expiresAt && Date.now() > code.expiresAt) {
    return {
      isValid: false,
      message: '测试码已过期，请联系管理员获取新的测试码'
    };
  }
  
  // 保存验证状态到localStorage，但不立即标记测试码为已使用
  // 引入"使用会话"概念：验证后24小时内可以多次进入测评页面
  // 根据测评类型分别保存验证状态
  localStorage.setItem(`verifiedCode_${assessmentType}`, inputCodeUpper);
  localStorage.setItem(`codeVerifiedAt_${assessmentType}`, Date.now().toString());
  localStorage.setItem(`codeSource_${assessmentType}`, isAdminCode ? 'admin' : 'builtin');
  
  // 为了兼容性，也保存到全局验证状态（用于情绪记忆测评的向后兼容）
  if (assessmentType === 'emotional-memory-pattern') {
    localStorage.setItem('verifiedCode', inputCodeUpper);
    localStorage.setItem('codeVerifiedAt', Date.now().toString());
    localStorage.setItem('codeSource', isAdminCode ? 'admin' : 'builtin');
  }
  
  // 注意：测试码暂时不标记为已使用，允许24小时内多次访问
  // 实际的使用标记将在用户完成测评或24小时后进行
  
  return {
    isValid: true,
    message: '验证成功',
    code: {
      code: code.code,
      isUsed: code.isUsed,
      usedAt: code.usedAt,
      expiresAt: code.expiresAt,
      assessmentType: code.assessmentType || 'emotional-memory-pattern'
    }
  };
};

// 检查当前验证状态
export const checkVerificationStatus = (assessmentType?: 'emotional-memory-pattern' | 'big-five-personality'): {
  isVerified: boolean;
  code?: string;
  verifiedAt?: number;
  isExpired: boolean;
} => {
  // 如果指定了测评类型，检查该类型的验证状态
  const storageKey = assessmentType ? `verifiedCode_${assessmentType}` : 'verifiedCode';
  const timeKey = assessmentType ? `codeVerifiedAt_${assessmentType}` : 'codeVerifiedAt';
  
  const verifiedCode = localStorage.getItem(storageKey);
  const codeVerifiedAt = localStorage.getItem(timeKey);
  
  if (!verifiedCode || !codeVerifiedAt) {
    return { isVerified: false, isExpired: false };
  }
  
  const verifiedTime = parseInt(codeVerifiedAt);
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  
  const isExpired = now - verifiedTime > twentyFourHours;
  
  return {
    isVerified: !isExpired,
    code: verifiedCode,
    verifiedAt: verifiedTime,
    isExpired
  };
};

// 清除验证状态
export const clearVerification = () => {
  localStorage.removeItem('verifiedCode');
  localStorage.removeItem('codeVerifiedAt');
  localStorage.removeItem('codeSource');
};

// 标记测试码为已使用（在用户完成测评时调用）
export const markTestCodeAsUsed = async (): Promise<void> => {
  const verifiedCode = localStorage.getItem('verifiedCode');
  const codeSource = localStorage.getItem('codeSource');
  
  if (!verifiedCode || !codeSource) {
    console.warn('没有找到已验证的测试码信息');
    return;
  }
  
  try {
    if (codeSource === 'admin') {
      // 更新管理员测试码状态
      await testCodeStorage.updateTestCode(verifiedCode, {
        isUsed: true,
        status: 'used',
        usedAt: new Date().toISOString()
      });
    } else {
      // 更新内置测试码状态
      const builtInCodes = getStoredCodes();
      const code = builtInCodes.find(c => c.code === verifiedCode);
      if (code) {
        code.isUsed = true;
        code.usedAt = Date.now();
        saveStoredCodes(builtInCodes);
      }
    }
    
    // 清除验证状态，强制用户下次需要输入新的测试码
    clearVerification();
    
    console.log('测试码已标记为已使用:', verifiedCode);
  } catch (error) {
    console.error('标记测试码为已使用失败:', error);
  }
};

// 检查并清理过期的验证状态
export const cleanupExpiredVerification = async (assessmentType?: 'emotional-memory-pattern' | 'big-five-personality'): Promise<void> => {
  const verificationStatus = checkVerificationStatus(assessmentType);
  
  if (verificationStatus.isExpired && verificationStatus.code) {
    console.log('验证状态已过期，标记测试码为已使用并清理');
    await markTestCodeAsUsed();
    
    // 清理对应测评类型的验证状态
    if (assessmentType) {
      localStorage.removeItem(`verifiedCode_${assessmentType}`);
      localStorage.removeItem(`codeVerifiedAt_${assessmentType}`);
      localStorage.removeItem(`codeSource_${assessmentType}`);
    }
  }
};

// 获取所有测试码（管理员功能）
export const getAllTestCodes = (): TestCode[] => {
  return getStoredCodes();
};

// 重置测试码状态（管理员功能）
export const resetTestCode = (code: string): boolean => {
  const codes = getStoredCodes();
  const targetCode = codes.find(c => c.code === code);
  
  if (targetCode) {
    targetCode.isUsed = false;
    targetCode.usedAt = undefined;
    saveStoredCodes(codes);
    return true;
  }
  
  return false;
};

// 添加新测试码（管理员功能）
export const addTestCode = (code: string, expiresAt?: number, assessmentType: 'emotional-memory-pattern' | 'big-five-personality' = 'emotional-memory-pattern'): boolean => {
  const codes = getStoredCodes();
  
  // 检查是否已存在
  if (codes.find(c => c.code === code.toUpperCase())) {
    return false;
  }
  
  codes.push({
    code: code.toUpperCase(),
    isUsed: false,
    expiresAt,
    assessmentType
  });
  
  saveStoredCodes(codes);
  return true;
};

// 检查推荐功能是否开启
export const isReferralEnabled = async (): Promise<boolean> => {
  return await globalSettings.getReferralEnabled();
};

// 生成推荐测试码（用户主动选择后）
export const generateReferralTestCodes = async (): Promise<string[]> => {
  if (!(await isReferralEnabled())) {
    throw new Error('推荐功能未开启');
  }
  
  const codes: string[] = [];
  const adminCodes = await testCodeStorage.getTestCodes();
  
  // 生成两个测试码
  for (let i = 0; i < 2; i++) {
    const code = 'REF' + Math.random().toString(36).substring(2, 5).toUpperCase();
    const newCode = {
      id: code,
      code,
      isUsed: false,
      status: 'available' as const,
      createdAt: new Date().toISOString(),
      source: 'auto' as const,
      assessmentType: 'emotional-memory-pattern' as const // 推荐测试码默认为情绪记忆测评
    };
    
    adminCodes.push(newCode);
    codes.push(code);
  }
  
  await testCodeStorage.saveTestCodes(adminCodes);
  return codes;
};